import { create } from "zustand";
import type { ClaudeRateLimits } from "@ide/shared";

/**
 * Uso das IAs por projeto, contado neste computador a cada resposta: tokens (e o custo estimado) do
 * Claude — pela conta da Anthropic da pessoa ou pela IA do Kompil — e pedidos do Copilot. As cotas
 * da conta (o quanto resta) vêm de quem cobra: o servidor do Copilot e os cabeçalhos da Anthropic.
 * Aqui fica o que nenhum dos dois informa: a divisão por projeto, por modelo e por recurso.
 */
export type UsageProvider = "claude" | "kompil" | "copilot";

export interface ProviderUsage {
  /** Mensagens ao chat ou turnos do agente. */
  requests: number;
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  /** Custo estimado em US$ (preços da API; só o Claude pela conta da pessoa). */
  cost: number;
  /** Copilot: pedidos premium estimados (multiplicador do modelo escolhido). */
  premium: number;
  /** Copilot: sugestões aceitas no editor. */
  accepted: number;
}

export interface ProjectUsage {
  name: string;
  /** Último uso (ms). */
  at: number;
  usage: Partial<Record<UsageProvider, ProviderUsage>>;
}

/** "AAAA-MM" → projeto → uso. */
type Months = Record<string, Record<string, ProjectUsage>>;
/** "AAAA-MM" → "provedor|model|nome" ou "provedor|feature|nome" → uso. */
type Breakdown = Record<string, Record<string, ProviderUsage>>;

interface Saved {
  months: Months;
  breakdown: Breakdown;
  /** Orçamento mensal (US$) da conta da Anthropic (null: sem orçamento). */
  claudeBudgetUsd: number | null;
}

export interface UsageMeta {
  /** Nome do projeto (as sugestões do Copilot só sabem o id). */
  name?: string;
  /** Modelo que respondeu (ex.: "claude-sonnet-5-5", "GPT-5"). */
  model?: string;
  /** Recurso que gastou: "Chat", "Agente", "Sugestões"… */
  feature?: string;
}

interface AiUsageState extends Saved {
  /** Limites por minuto da conta na Anthropic (cabeçalhos da última resposta). */
  claudeLimits: ClaudeRateLimits | null;
  names: Record<string, string>;
  record(provider: UsageProvider, projectId: string, delta: Partial<ProviderUsage>, meta?: UsageMeta): void;
  nameProject(projectId: string, name: string): void;
  setClaudeLimits(limits: ClaudeRateLimits): void;
  setClaudeBudget(usd: number | null): void;
}

const KEY = "kompil.aiUsage.v2";
/** Meses guardados (o atual e os anteriores). */
const KEEP_MONTHS = 6;

export const EMPTY_USAGE: ProviderUsage = {
  requests: 0,
  input: 0,
  output: 0,
  cacheRead: 0,
  cacheWrite: 0,
  cost: 0,
  premium: 0,
  accepted: 0,
};

export const monthKey = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const isObject = (v: unknown): v is Record<string, never> => Boolean(v) && typeof v === "object";

function read(): Saved {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<Saved> | null;
    return {
      months: isObject(raw?.months) ? raw.months : {},
      breakdown: isObject(raw?.breakdown) ? raw.breakdown : {},
      claudeBudgetUsd:
        typeof raw?.claudeBudgetUsd === "number" && raw.claudeBudgetUsd > 0 ? raw.claudeBudgetUsd : null,
    };
  } catch {
    return { months: {}, breakdown: {}, claudeBudgetUsd: null };
  }
}

function write(saved: Saved) {
  try {
    localStorage.setItem(KEY, JSON.stringify(saved));
  } catch {
    // sem armazenamento: o uso vale só nesta sessão
  }
}

/** Soma um uso a outro (só números finitos e positivos). */
export function addUsage(base: ProviderUsage | undefined, delta: Partial<ProviderUsage>): ProviderUsage {
  const out = { ...EMPTY_USAGE, ...base };
  for (const k of Object.keys(EMPTY_USAGE) as Array<keyof ProviderUsage>) {
    const v = delta[k];
    if (typeof v === "number" && Number.isFinite(v) && v > 0) out[k] += v;
  }
  return out;
}

/** Só os últimos meses. */
export function pruneMonths<T>(months: Record<string, T>, keep = KEEP_MONTHS): Record<string, T> {
  const keys = Object.keys(months).sort().slice(-keep);
  return Object.fromEntries(keys.map((k) => [k, months[k]!]));
}

export const useAiUsage = create<AiUsageState>((set, get) => ({
  ...read(),
  claudeLimits: null,
  names: {},
  record: (provider, projectId, delta, meta = {}) => {
    const month = monthKey();
    const s = get();
    const projects = { ...(s.months[month] ?? {}) };
    const current = projects[projectId];
    projects[projectId] = {
      name: meta.name ?? s.names[projectId] ?? current?.name ?? "Projeto",
      at: Date.now(),
      usage: { ...current?.usage, [provider]: addUsage(current?.usage[provider], delta) },
    };
    const parts = { ...(s.breakdown[month] ?? {}) };
    if (meta.model)
      parts[`${provider}|model|${meta.model}`] = addUsage(parts[`${provider}|model|${meta.model}`], delta);
    if (meta.feature)
      parts[`${provider}|feature|${meta.feature}`] = addUsage(
        parts[`${provider}|feature|${meta.feature}`],
        delta,
      );
    const months = pruneMonths({ ...s.months, [month]: projects });
    const breakdown = pruneMonths({ ...s.breakdown, [month]: parts });
    set({ months, breakdown });
    write({ months, breakdown, claudeBudgetUsd: s.claudeBudgetUsd });
  },
  nameProject: (projectId, name) => {
    if (get().names[projectId] === name) return;
    set((s) => ({ names: { ...s.names, [projectId]: name } }));
  },
  setClaudeLimits: (claudeLimits) => set({ claudeLimits }),
  setClaudeBudget: (usd) => {
    const claudeBudgetUsd = usd && usd > 0 ? usd : null;
    set({ claudeBudgetUsd });
    write({ months: get().months, breakdown: get().breakdown, claudeBudgetUsd });
  },
}));

/** Tokens de um uso (entrada + saída + cache). */
export const totalTokens = (u: ProviderUsage | undefined) =>
  u ? u.input + u.output + u.cacheRead + u.cacheWrite : 0;

export type UsageRow = { projectId: string; name: string; usage: ProviderUsage };

/** Linhas por projeto dos provedores no mês, do que mais usou ao que menos usou. */
export function usageRows(months: Months, providers: UsageProvider[], month = monthKey()): UsageRow[] {
  const rows: UsageRow[] = [];
  for (const [projectId, p] of Object.entries(months[month] ?? {})) {
    let usage: ProviderUsage | undefined;
    for (const provider of providers) {
      const u = p.usage[provider];
      if (u) usage = addUsage(usage, u);
    }
    if (usage && (usage.requests || usage.accepted || totalTokens(usage)))
      rows.push({ projectId, name: p.name, usage });
  }
  return rows.sort(
    (a, b) => totalTokens(b.usage) + b.usage.requests - (totalTokens(a.usage) + a.usage.requests),
  );
}

/** Soma de todos os projetos no mês. */
export function monthTotal(months: Months, providers: UsageProvider[], month = monthKey()): ProviderUsage {
  return usageRows(months, providers, month).reduce((acc, r) => addUsage(acc, r.usage), EMPTY_USAGE);
}

/** Uso do mês por modelo ou por recurso (do maior para o menor). */
export function breakdownRows(
  breakdown: Breakdown,
  provider: UsageProvider,
  kind: "model" | "feature",
  month = monthKey(),
): Array<{ name: string; usage: ProviderUsage }> {
  const prefix = `${provider}|${kind}|`;
  return Object.entries(breakdown[month] ?? {})
    .filter(([k]) => k.startsWith(prefix))
    .map(([k, usage]) => ({ name: k.slice(prefix.length), usage }))
    .sort(
      (a, b) =>
        b.usage.cost - a.usage.cost ||
        totalTokens(b.usage) - totalTokens(a.usage) ||
        b.usage.requests - a.usage.requests,
    );
}

/** "0.33x" → 0.33; "1x" → 1; sem informação, 1 (o padrão dos planos pagos). */
export function premiumMultiplier(usage: string | undefined): number {
  if (!usage) return 1;
  const n = Number.parseFloat(usage.replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : 1;
}

const compact = new Intl.NumberFormat("pt-BR", { notation: "compact", maximumFractionDigits: 1 });
const whole = new Intl.NumberFormat("pt-BR");
const usd = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const usdSmall = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 4,
});

/** 850 · 12,3 mil · 1,2 mi */
export const formatTokens = (n: number) => (n < 10_000 ? whole.format(Math.round(n)) : compact.format(n));
export const formatCount = (n: number) => whole.format(Math.round(n * 100) / 100);
/** US$ 3,40 (centavos de centavo quando é pouco). */
export const formatUsd = (n: number) => (n > 0 && n < 0.01 ? usdSmall.format(n) : usd.format(n));
